﻿import * as React from 'react';

import { Form, FormControlProps } from 'react-bootstrap';
import { ToolTip } from 'components/ToolTip/ToolTip';
import { useIntl } from 'react-intl';

import 'components/GMetrixInput/gmetrix-input.scss';

import { useStableId } from 'hooks';

interface GMetrixInputProps extends FormControlProps {
    autoFocus?: boolean;
    autoSize?: boolean;
    dataList?: any[];
    feedbackMessage?: string;
    infoTitle?: string;
    label?: string;
    labelClass?: string;
    max?: number;
    maxLength?: number;
    min?: number;
    readOnlyNoLock?: boolean;
    required?: boolean;
    rows?: number;
    size?: 'sm' | 'lg' | undefined;
    subLabel?: string;
    type?: string;
    validType?: 'invalid' | 'valid' | undefined;
    width?: string;
}

function GMetrixInput({
    autoFocus = false,
    autoSize,
    dataList,
    feedbackMessage,
    infoTitle,
    label,
    labelClass,
    max,
    maxLength,
    min = 0,
    onChange,
    readOnly,
    readOnlyNoLock = false,
    required = false,
    rows,
    size = undefined,
    subLabel,
    type = 'text',
    validType,
    value,
    width = 'w-100',
    ...otherProps
}: GMetrixInputProps) {

    const { formatMessage } = useIntl();
    const inputId = useStableId('gmx-input');
    const feedbackId = `${inputId}-feedback`;
    const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const pendingChangeRef = React.useRef(false);
    const pendingSelectionRef = React.useRef<{ end: number; start: number } | null>(null);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {

        const { selectionEnd, selectionStart } = event.target;

        pendingChangeRef.current = true;
        if (selectionStart !== null && selectionEnd !== null) {

            pendingSelectionRef.current = {
                end: selectionEnd,
                start: selectionStart
            };

        }

        onChange?.(event);

    };

    React.useLayoutEffect(() => {

        if (!pendingChangeRef.current) {

            return;

        }

        const element = inputRef.current;
        const pendingSelection = pendingSelectionRef.current;

        pendingChangeRef.current = false;

        if (!element) {

            pendingSelectionRef.current = null;
            return;

        }

        if (document.activeElement !== element) {

            element.focus({ preventScroll: true });

        }

        if (!pendingSelection) {

            return;

        }

        const maxSelectionPosition = element.value.length;
        const start = Math.min(pendingSelection.start, maxSelectionPosition);
        const end = Math.min(pendingSelection.end, maxSelectionPosition);

        try {

            element.setSelectionRange(start, end);

        } catch {
        }

        pendingSelectionRef.current = null;

    }, [value]);

    return (
        <div className={`joyride-search ${width}`}>
            {label && (
                <div className="d-flex align-items-center">
                    <Form.Label className={`fw-bold ${labelClass ? labelClass : ''}`} htmlFor={inputId}>
                        {label + (required ? '*' : '')}
                        {readOnly && !readOnlyNoLock && <i className="p-1 ms-1 fa-solid fa-lock" title={formatMessage({ id: 'read_only.04ea3de4', defaultMessage: 'Read Only' })} />}
                    </Form.Label>
                    {subLabel && (
                    <small className="text-muted ms-2 mb-2">
                        {subLabel}
                    </small>
                    )}
                    {infoTitle && <ToolTip className="mb-1" content={infoTitle} />}
                </div>
            )}
            <Form.Control
                aria-describedby={feedbackMessage ? feedbackId : undefined}
                aria-invalid={validType === 'invalid' ? true : undefined}
                aria-required={required ? true : undefined}
                autoFocus={autoFocus}
                className={`m-0 w-100`}
                disabled={readOnly || readOnlyNoLock}
                id={inputId}
                isInvalid={validType === 'invalid'}
                isValid={validType === 'valid'}
                list="datalistOptions"
                max={max}
                maxLength={maxLength}
                min={min}
                onChange={handleChange}
                readOnly={readOnly || readOnlyNoLock}
                ref={inputRef}
                required={required}
                rows={rows}
                size={size}
                type={type}
                value={value}
                {...otherProps}
            />

            <Form.Control.Feedback id={feedbackId} type={validType}>
                {feedbackMessage}
            </Form.Control.Feedback>

            {dataList && (
                <datalist id="datalistOptions">
                    {dataList.map((item, index) => (
                        <option key={item.Name + index} value={item.Name}>
                            {item.Name}
                        </option>
                    ))}
                </datalist>
            )}
        </div>
    );

}

export { GMetrixInput };
